import {
  Component,
  contentChild,
  computed,
  inject,
  input,
  booleanAttribute,
  ViewEncapsulation,
} from '@angular/core';

import { _IdGenerator } from '@angular/cdk/a11y';
import { NttLabel } from './directives/label';
import { NttLeading } from './directives/leading';
import { NttPrefix } from './directives/prefix';
import { NttSuffix } from './directives/suffix';
import { NttTrailing } from './directives/trailing';
import { NttHint } from './directives/hint';
import { NttInput } from './directives/input';
import { NttLabelLeading } from './directives/label-leading';

import { _resolveFirstValidationMessage } from '../core';
import { NTT_VALIDATION_MESSAGES } from './validation-messages.token';

@Component({
  selector: 'ntt-form-field',
  templateUrl: './form-field.html',
  styleUrl: './form-field.scss',
  encapsulation: ViewEncapsulation.None,
})
export class NttFormField {
  private _idGenerator = inject(_IdGenerator);
  readonly inputId = this._idGenerator.getId('ntt-form-field-');

  readonly size = input<'small' | 'large'>('large');
  readonly required = input(false, { transform: booleanAttribute });
  readonly showErrorMessage = input<boolean>(true);

  private readonly _labelChild = contentChild(NttLabel);
  private readonly _labelLeadingChild = contentChild(NttLabelLeading);
  private readonly _leadingChild = contentChild(NttLeading);
  private readonly _prefixChild = contentChild(NttPrefix);
  private readonly _suffixChild = contentChild(NttSuffix);
  private readonly _trailingChild = contentChild(NttTrailing);
  private readonly _hintChild = contentChild(NttHint);
  private readonly _inputChild = contentChild(NttInput);

  private readonly _globalMessages = inject(NTT_VALIDATION_MESSAGES);

  readonly _isTouched = computed(
    () => this._inputChild()?.isTouched() ?? false,
  );
  readonly _isDirty = computed(
    () => this._inputChild()?.state()?.dirty() ?? false,
  );
  readonly _isDisabled = computed(
    () => this._inputChild()?.isDisabled() ?? false,
  );
  readonly _isInvalid = computed(
    () => this._inputChild()?.isInvalid() ?? false,
  );
  readonly _shouldShowError = computed(
    () => this._inputChild()?.shouldShowErrors() ?? false,
  );
  readonly _errors = computed(() => this._inputChild()?.errors());

  readonly _firstErrorMessage = computed(() => {
    return _resolveFirstValidationMessage(this._errors(), this._globalMessages);
  });

  readonly _hasLabel = computed(() => !!this._labelChild());
  readonly _hasLabelLeading = computed(() => !!this._labelLeadingChild());
  readonly _hasLeading = computed(() => !!this._leadingChild());
  readonly _hasPrefix = computed(() => !!this._prefixChild());
  readonly _hasSuffix = computed(() => !!this._suffixChild());
  readonly _hasTrailing = computed(() => !!this._trailingChild());
  readonly _hasHint = computed(() => !!this._hintChild());

  readonly _isEmpty = computed(() => {
    const val = this._inputChild()?.state()?.value();
    return !val || (typeof val === 'string' && val.trim() === '');
  });
}
